import { Group, Material, Mesh, MeshStandardMaterial, Texture, Vector3 } from 'three/webgpu'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'

/** Every load owns its resources, so old contracts can safely release them. */
export function disposeBargeModel(group: Group): void {
  const geometries = new Set<Mesh['geometry']>()
  const materials = new Set<Material>()
  const textures = new Set<Texture>()
  group.traverse(object => {
    if (!(object as Mesh).isMesh) return
    const mesh = object as Mesh
    geometries.add(mesh.geometry)
    for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      materials.add(material)
      for (const value of Object.values(material)) if (value instanceof Texture) textures.add(value)
    }
  })
  for (const geometry of geometries) geometry.dispose()
  for (const material of materials) material.dispose()
  for (const texture of textures) texture.dispose()
  group.clear()
}

/** Blender-authored deck barge; metres, Y-up, +Z bow, waterline origin. */
export async function loadContainerBargeModel(seed = 1): Promise<Group> {
  const { scene } = await new GLTFLoader().loadAsync('/models/container-barge/container-barge.glb')
  const vessel = new Group()
  vessel.name = 'container-barge-blender'
  scene.rotation.y = -Math.PI / 2
  vessel.add(scene)
  vessel.updateMatrixWorld(true)
  const towPoint = vessel.getObjectByName('tow_point_bow')
  if (!towPoint || towPoint.getWorldPosition(new Vector3()).distanceTo(new Vector3(0, 3.3, 106)) > .01) {
    disposeBargeModel(vessel)
    throw new Error('Container barge bow attachment does not match the towing rig')
  }
  const cargo = new Set<MeshStandardMaterial>()
  vessel.traverse(object => {
    if (!(object as Mesh).isMesh) return
    object.castShadow = true
    object.receiveShadow = true
    const material = (object as Mesh).material
    for (const mat of Array.isArray(material) ? material : [material]) {
      if (mat.name.startsWith('Cargo ') && mat instanceof MeshStandardMaterial) cargo.add(mat)
    }
  })
  // Keep deterministic colour variety per contract without extra draw calls.
  const materials = [...cargo].sort((a, b) => a.name.localeCompare(b.name))
  const colours = materials.map(material => material.color.clone())
  const offset = ((Math.trunc(seed) - 1) % materials.length + materials.length) % materials.length
  materials.forEach((material, i) => material.color.copy(colours[(i + offset) % colours.length]))
  return vessel
}
